/**
 * ResetPasswordPage — shadcn/ui redesign
 *
 * Visual changes (zero business-logic change):
 *   • All MUI imports removed; shadcn/ui + native HTML only
 *   • Consistent split-panel layout matching LoginPage + ForgotPasswordPage
 *   • Three states: verifying → invalid token → form / success (logic unchanged)
 *   • Password show/hide toggle (Eye / EyeOff) shared across both fields
 *   • Loader2 spinner inline on button
 *   • shadcn Alert variants: destructive, success, warning, default
 *   • Password requirements in a visually contained muted box
 *   • Auto-redirect to /login after success (3 s) — unchanged
 *
 * API calls unchanged:
 *   POST /password/verify-reset-token?token=…
 *   POST /password/reset-password
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Lock,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ShieldAlert,
} from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';

/** Shared left brand panel — identical to LoginPage */
const BrandPanel: React.FC = () => (
  <div className="hidden lg:flex flex-col justify-between bg-primary p-12 text-primary-foreground">
    <div className="flex items-center gap-2.5">
      <img
        src="/logo/logo36.png"
        alt="Converse"
        className="h-8 w-8 object-contain brightness-0 invert"
      />
      <span className="text-lg font-semibold tracking-tight">Converse</span>
    </div>
    <div className="space-y-3">
      <blockquote className="text-2xl font-semibold leading-snug">
        {/* "4tform your team actually wants to use." */}
      </blockquote>
      <p className="text-sm text-primary-foreground/70">
        {/* Trusted by support teams across 10+ industries */}
      </p>
    </div>
    <p className="text-xs text-primary-foreground/50">
      © {new Date().getFullYear()} Dure Technologies Pvt Ltd
    </p>
  </div>
);

/** Shared full-screen overlay for verifying / error states */
const CenteredPanel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-svh grid lg:grid-cols-2">
    <BrandPanel />
    <div className="flex items-center justify-center bg-background p-8 md:p-12">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-3 lg:hidden">
          <img src="/logo/logo36.png" alt="Converse" className="h-10 w-10 object-contain" />
          <span className="text-lg font-semibold tracking-tight">Converse</span>
        </div>
        {children}
      </div>
    </div>
  </div>
);

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword]       = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword]     = useState(false);
  const [loading, setLoading]               = useState(false);
  const [verifying, setVerifying]           = useState(true);
  const [tokenValid, setTokenValid]         = useState(false);
  const [success, setSuccess]               = useState(false);
  const [error, setError]                   = useState('');
  const [tokenEmail, setTokenEmail]         = useState('');

  useEffect(() => { verifyToken(); }, [token]);

  const verifyToken = async () => {
    if (!token) {
      setError('Invalid password reset link. Please request a new one.');
      setVerifying(false);
      return;
    }
    try {
      const response = await api.post(`/password/verify-reset-token?token=${token}`);
      if (response.data.valid) {
        setTokenValid(true);
        setTokenEmail(response.data.email);
      } else {
        setError(response.data.message || 'Invalid or expired reset token');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to verify reset token');
    } finally {
      setVerifying(false);
    }
  };

  const validatePassword = () => {
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validatePassword()) return;
    setLoading(true);
    try {
      const response = await api.post('/password/reset-password', {
        token,
        new_password: newPassword,
      });
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Verifying state ─────────────────────────────────────────────────────────

  if (verifying) {
    return (
      <CenteredPanel>
        <div className="flex flex-col items-center gap-4 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <div className="space-y-1">
            <h1 className="text-xl font-semibold tracking-tight">Verifying reset link</h1>
            <p className="text-sm text-muted-foreground">Please wait a moment…</p>
          </div>
        </div>
      </CenteredPanel>
    );
  }

  // ── Invalid token state ─────────────────────────────────────────────────────

  if (!tokenValid) {
    return (
      <CenteredPanel>
        <div className="space-y-1 text-center lg:text-left">
          <div className="flex justify-center lg:justify-start mb-3">
            <div className="rounded-full bg-destructive/10 p-3">
              <ShieldAlert className="h-6 w-6 text-destructive" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Invalid reset link</h1>
          <p className="text-sm text-muted-foreground">
            {error || 'This password reset link is invalid or has expired.'}
          </p>
        </div>

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Reset links expire after <span className="font-medium">1 hour</span>.
            Please request a new one.
          </AlertDescription>
        </Alert>

        <div className="flex flex-col gap-2">
          <Button asChild>
            <Link to="/forgot-password">Request new reset link</Link>
          </Button>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:underline hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to login
          </Link>
        </div>
      </CenteredPanel>
    );
  }

  // ── Main form + success ─────────────────────────────────────────────────────

  return (
    <div className="min-h-svh grid lg:grid-cols-2">
      <BrandPanel />

      <div className="flex items-center justify-center bg-background p-8 md:p-12">
        <div className="w-full max-w-sm space-y-6">

          {/* Mobile logo */}
          <div className="flex flex-col items-center gap-3 lg:hidden">
            <img src="/logo/logo36.png" alt="Converse" className="h-10 w-10 object-contain" />
            <span className="text-lg font-semibold tracking-tight">Converse</span>
          </div>

          {!success ? (
            /* ── Reset form ────────────────────────────────────────────── */
            <>
              {/* Icon + heading */}
              <div className="space-y-1 text-center lg:text-left">
                <div className="flex justify-center lg:justify-start mb-3">
                  <div className="rounded-full bg-primary/10 p-3">
                    <Lock className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <h1 className="text-2xl font-semibold tracking-tight">Reset your password</h1>
                <p className="text-sm text-muted-foreground">
                  Creating a new password for{' '}
                  <span className="font-medium text-foreground">{tokenEmail}</span>
                </p>
              </div>

              {/* Error */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* New password */}
                <div className="space-y-2">
                  <Label htmlFor="new-password">New password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      autoFocus
                      disabled={loading}
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                {/* Confirm password */}
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm new password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                      className="pr-10"
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                {/* Requirements box */}
                <div className="rounded-md border border-dashed border-border bg-muted/40 px-3 py-2.5 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Password requirements</p>
                  <ul className="text-xs text-muted-foreground space-y-0.5 list-disc list-inside">
                    <li>At least 8 characters</li>
                    <li>Mix of letters, numbers and symbols</li>
                    <li>Don't reuse old passwords</li>
                  </ul>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Reset Password
                </Button>
              </form>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:underline hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to login
                </Link>
              </div>
            </>
          ) : (
            /* ── Success state ───────────────────────────────────────── */
            <>
              {/* Icon + heading */}
              <div className="space-y-1 text-center lg:text-left">
                <div className="flex justify-center lg:justify-start mb-3">
                  <div className="rounded-full bg-emerald-100 p-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
                <h1 className="text-2xl font-semibold tracking-tight">Password reset!</h1>
                <p className="text-sm text-muted-foreground">
                  Your password has been reset successfully.
                </p>
              </div>

              <Alert variant="success">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>All done</AlertTitle>
                <AlertDescription>
                  Redirecting you to the login page in 3 seconds…
                </AlertDescription>
              </Alert>

              <Button asChild className="w-full">
                <Link to="/login">Go to login now</Link>
              </Button>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
